import { useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import "./components/partner/partner.css";
import { InsuranceCard, RateCard, ReferralCard } from "./components/partner/PartnerExtras";
import { AgreementCard, ReadinessCard } from "./components/partner/AgreementCard";

const API = process.env.REACT_APP_BACKEND_URL;
const call = async (path, body) => {
  const r = await fetch(`${API}/api/trade-network/partner${path}`, body === undefined ? {} : {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Something went wrong. Please try again.");
  return d;
};

const ENTITY = [["sole_proprietor", "Sole proprietor"], ["1099_contractor", "1099 independent contractor"],
  ["llc", "LLC / small business"], ["crew_lead", "Crew lead (bringing a crew)"]];

const Steps = ({ p }) => {
  const s = [["Terms + trade profile", p.terms_accepted], ["Payout setup (Stripe)", p.onboarding_completed], ["On the ready roster", p.onboarding_completed]];
  return (
    <ol className="pw-steps" data-testid="partner-steps">
      {s.map(([l, done], i) => <li key={l} className={done ? "done" : ""}><span className="pw-dot">{done ? "✓" : i + 1}</span>{l}</li>)}
    </ol>
  );
};

const TermsForm = ({ p, onDone }) => {
  const [f, setF] = useState({ full_name: p.contact_name || "", business_name: "", entity_type: "sole_proprietor", trade: p.trade || "",
    phone: p.contact_phone || "", email: p.contact_email || "", service_area: "DFW", license_no: "", has_insurance: false, agree: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await call(`/${p.partner_id}/accept-terms`, f); onDone(); } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  const field = (k, label, props = {}) => (
    <label className="pw-field">{label}<input value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`partner-${k}`} {...props} /></label>
  );
  return (
    <form className="pw-card" onSubmit={submit} data-testid="partner-terms-form">
      <h2 className="pw-h2">Step 1 · Partnership terms</h2>
      <ul className="pw-terms" data-testid="partner-terms-list">{p.terms.map((t) => <li key={t}>{t}</li>)}</ul>
      <h2 className="pw-h2">Your trade profile</h2>
      <div className="pw-grid">
        {field("full_name", "Full name", { required: true })}
        {field("business_name", "Business name (if any)")}
        <label className="pw-field">How you work
          <select value={f.entity_type} onChange={(e) => set("entity_type", e.target.value)} data-testid="partner-entity_type">
            {ENTITY.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        {field("trade", "Primary trade (e.g. painting, flooring, HVAC)", { required: true })}
        {field("phone", "Mobile phone", { required: true, type: "tel" })}
        {field("email", "Email", { required: true, type: "email" })}
        {field("service_area", "Service area")}
        {field("license_no", "License # (if your trade requires one)")}
      </div>
      <label className="pw-check"><input type="checkbox" checked={f.has_insurance} onChange={(e) => set("has_insurance", e.target.checked)} data-testid="partner-has_insurance" /> I carry general liability insurance (we'll collect the certificate before your first work order)</label>
      <label className="pw-check"><input type="checkbox" checked={f.agree} onChange={(e) => set("agree", e.target.checked)} data-testid="partner-agree" /> I have read and accept the partnership terms above</label>
      {err && <div className="pw-err" data-testid="partner-error">{err}</div>}
      <button className="pw-btn" disabled={busy || !f.agree} data-testid="partner-accept-btn">{busy ? "Saving…" : "Accept terms & continue"}</button>
    </form>
  );
};

const PayoutStep = ({ p }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async () => {
    setBusy(true); setErr("");
    try { window.location.href = (await call(`/${p.partner_id}/payout-link`, {})).url; } catch (x) { setErr(x.message); setBusy(false); }
  };
  return (
    <div className="pw-card" data-testid="partner-payout-step">
      <h2 className="pw-h2">Step 2 · Set up your payouts</h2>
      <p className="pw-p">Payouts go straight to your bank through Stripe, the payment processor trusted by millions of businesses. It takes about 3 minutes. Have your bank routing/account number and the last 4 of your SSN or your EIN ready.</p>
      {err && <div className="pw-err" data-testid="partner-error">{err}</div>}
      <button className="pw-btn" onClick={go} disabled={busy} data-testid="partner-payout-btn">{busy ? "Opening Stripe…" : p.payout_started ? "Continue payout setup" : "Set up payouts with Stripe"}</button>
    </div>
  );
};

const Ready = ({ p }) => (
  <div className="pw-card pw-card--ok" data-testid="partner-ready">
    <h2 className="pw-h2">You're on the Moovin Forward ready roster.</h2>
    <p className="pw-p">Welcome aboard{p.first_name ? `, ${p.first_name}` : ""}. Here's what happens next:</p>
    <ul className="pw-terms">
      <li>When a unit in your trade and area is authorized, you'll receive a work order offer by email with the scope, property and response window.</li>
      <li>Open the secure link to review the scope and submit your bid. Once accepted, you receive a signed work order and the photo checklist.</li>
      <li>You never front material costs. 50% of your mobilization (plus approved courier mileage) is released at job assignment; the remaining 50% is released after the final detail cleaning pass is verified.</li>
    </ul>
    <p className="pw-p pw-muted">Questions? Email hello@moovinforward.org.</p>
  </div>
);

export default function PartnerWelcome() {
  const { partnerId } = useParams();
  const { pathname } = useLocation();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try { setP(await call(`/${partnerId}`)); } catch (x) { setErr(x.message); }
  }, [partnerId]);

  useEffect(() => {
    (async () => {
      if (pathname.endsWith("/refresh")) {
        try { window.location.href = (await call(`/${partnerId}/payout-link`, {})).url; return; } catch (x) { setNote(x.message); }
      }
      if (pathname.endsWith("/onboarded")) {
        try {
          const r = await call(`/${partnerId}/stripe-return`, {});
          if (!r.onboarding_completed) setNote("Stripe still needs a few details. Tap continue to finish.");
        } catch (x) { setNote(x.message); }
      }
      load();
    })();
  }, [pathname, partnerId, load]);
  const indep = p?.readiness?.tier === "independent";

  return (
    <div className="pw-page" data-testid="partner-welcome-page">
      <header className="pw-head">
        <div className="pw-brand">MOOVIN FORWARD LLC</div>
        <div className="pw-sub">Trade Partnership · Dallas–Fort Worth</div>
      </header>
      {err && <div className="pw-card pw-err" data-testid="partner-load-error">{err}</div>}
      {!p && !err && <div className="pw-p pw-muted" data-testid="partner-loading">Loading your invitation…</div>}
      {p && (
        <main className="pw-main">
          <h1 className="pw-h1" data-testid="partner-greeting">Teaming up for high-impact asset stabilization across DFW</h1>
          <p className="pw-lead">{p.first_name ? `${p.first_name}, welcome. ` : "Welcome. "}We are an ROI stabilization and asset turnaround firm, <b>not</b> a temp agency or brokerage. We partner directly with skilled local trades and small businesses to deliver rapid, pristine property stabilization, and we build our ready roster <b>before</b> contracts land so the work is waiting for you.</p>
          <p className="pw-lead" data-testid="partner-win-win">The win-win: fair, transparent compensation, predictable workflows, direct digital work orders in our platform app, and secure, encrypted payouts through Stripe.</p>
          <p className="pw-lead" data-testid="partner-commitment">Strict professionalism is our hallmark. We are dedicated to elevating local trade professionals through structured workflows, predictable timelines and transparent fiduciary practices.</p>
          <div className="pw-pillars">
            <div><b>Zero markup</b><span>Materials at documented cost. Every line item is visible to you.</span></div>
            <div><b>Two-stage mobilization</b><span>50% at job assignment, 50% after the verified final detail clean. Materials procured for you.</span></div>
            <div><b>Steady institutional work</b><span>Multi-unit waves, scheduled in advance.</span></div>
          </div>
          <Steps p={p} />
          {note && <div className="pw-note" data-testid="partner-note">{note}</div>}
          {p.expired && !p.terms_accepted ? (
            <div className="pw-card pw-err" data-testid="partner-expired">This invitation expired after 14 days. Reply to the original text or email and we'll send you a fresh link.</div>
          ) : p.onboarding_completed ? (
            <><Ready p={p} /><ReadinessCard p={p} /><AgreementCard p={p} onSigned={load} />{indep && <AgreementCard p={p} kind="release" onSigned={load} />}<InsuranceCard p={p} onSaved={load} /><RateCard /><ReferralCard p={p} onSaved={load} /></>
          ) : p.terms_accepted ? <><ReadinessCard p={p} /><AgreementCard p={p} onSigned={load} />{indep && <AgreementCard p={p} kind="release" onSigned={load} />}<PayoutStep p={p} /><InsuranceCard p={p} onSaved={load} /></> : <><RateCard /><TermsForm p={p} onDone={load} /></>}
        </main>
      )}
      <footer className="pw-foot">Moovin Forward LLC · moovinforward.org · Protected under Texas CPRC §134A and the Defend Trade Secrets Act</footer>
    </div>
  );
}
